import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Card,
  Alert,
  CircularProgress,
  Button,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { alpha } from "@mui/material/styles";
import { logPayment } from "./payments";

export const BOOST_OPTIONS = [
  { days: 3, price: 5, label: "3 días" },
  { days: 7, price: 10, label: "7 días" },
  { days: 15, price: 18, label: "15 días" },
];

const paypalOptions = {
  "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
  currency: "USD",
  intent: "capture",
};

const BoostProjectDialog = ({ open, onClose, projectId, onBoosted }) => {
  const [selected, setSelected] = useState(BOOST_OPTIONS[1]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paypalKey, setPaypalKey] = useState(0);

  const handleSelect = (option) => {
    setSelected(option);
    setPaypalKey((k) => k + 1);
    setError(null);
  };

  const handleCreateOrder = (data, actions) => {
    return actions.order.create({
      purchase_units: [
        {
          description: `Destacar proyecto por ${selected.label}`,
          amount: { value: String(selected.price), currency_code: "USD" },
        },
      ],
    });
  };

  const handleApprove = async (data, actions) => {
    try {
      setLoading(true);
      await actions.order.capture();

      const featuredUntil = new Date();
      featuredUntil.setDate(featuredUntil.getDate() + selected.days);

      await updateDoc(doc(db, "projects", projectId), {
        featured: true,
        featuredUntil,
      });

      await logPayment({
        type: "boost",
        amount: selected.price,
        description: `Destacar proyecto por ${selected.label}`,
      });

      onBoosted && onBoosted(featuredUntil);
      onClose();
    } catch (err) {
      console.error("Error boosting project:", err);
      setError("Ocurrió un error al procesar el pago. Intentá de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <StarIcon sx={{ color: "#d4a017" }} />
        Destacar proyecto
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Tu proyecto aparece primero en los resultados de búsqueda mientras dure el destaque.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
          {BOOST_OPTIONS.map((option) => (
            <Card
              key={option.days}
              onClick={() => handleSelect(option)}
              sx={{
                flex: 1,
                cursor: "pointer",
                textAlign: "center",
                p: 1.5,
                border: 2,
                borderColor: selected.days === option.days ? "primary.main" : "divider",
              }}
            >
              <Typography variant="subtitle2">{option.label}</Typography>
              <Typography variant="h6">USD$ {option.price}</Typography>
            </Card>
          ))}
        </Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <PayPalScriptProvider options={paypalOptions}>
          <Box sx={{ position: "relative", minHeight: 45 }}>
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: (theme) => alpha(theme.palette.background.paper, 0.85),
                  zIndex: 1,
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}
            <PayPalButtons
              key={paypalKey}
              style={{ shape: "rect", layout: "vertical", label: "pay" }}
              createOrder={handleCreateOrder}
              onApprove={handleApprove}
              onError={() => setError("Ocurrió un error con PayPal.")}
            />
          </Box>
        </PayPalScriptProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BoostProjectDialog;
